import { createTheme, Theme } from '@mui/material/styles';
import { ThemeConfig, ThemeNames } from '@tenants/types';

/**
 * Creates a per-tenant MUI theme from a ThemeConfig.
 *
 * Scoped intentionally to MuiButton only — this is the minimum needed to make
 * atomic button components (PillCtaButton, etc.) pick up the correct tenant
 * primary color without manual sx overrides at the UI/section layer.
 *
 * Do NOT expand this to MuiCard, MuiSkeleton, typography, etc. without a
 * dedicated baseline + review — those components have existing per-tenant
 * ThemeProvider overrides that need to be migrated carefully.
 *
 * Tenant token → MUI mapping:
 *   colors.primary         → palette.primary.main  (containedPrimary fill)
 *   colors.primaryVeryDark → palette.primary.dark  (containedPrimary hover)
 *   colors.primaryLight    → palette.primary.light  (outlinedPrimary hover bg)
 *   colors.primaryDark      → palette.text.primary  (sx color: 'text.primary')
 *   buttons.borderRadiusLarge → MuiButton root borderRadius
 */
export function createMuiThemeFromTenant(theme: ThemeConfig<ThemeNames>): Theme {
  const { colors, buttons } = theme;

  return createTheme({
    palette: {
      primary: {
        main: colors.primary,
        light: colors.primaryLight,
        dark: colors.primaryVeryDark,
      },
      text: {
        primary: colors.primaryDark as string,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: buttons.borderRadiusLarge,
            textTransform: 'none',
          },
          containedPrimary: {
            backgroundColor: colors.primary,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: colors.primaryVeryDark,
            },
          },
          outlinedPrimary: {
            borderColor: colors.primary,
            color: colors.primary,
            '&:hover': {
              borderColor: colors.primary,
              backgroundColor: colors.primaryLight,
            },
          },
        },
      },
    },
  });
}
